<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import {
  GraduationCap,
  Newspaper,
  Wrench,
  FolderKanban,
  ChevronDown,
  Menu,
  X,
  Calculator,
  UserCircle2,
  LogOut,
  Settings,
  ShieldCheck,
  MessageCircle,
  TrendingUp,
} from "@lucide/vue";
import ThemeToggle from "./ThemeToggle.vue";
import { useAuthStore } from "@/stores/auth";
import { apiFetch } from "@/utils/api";

interface NavChild {
  label: string;
  href?: string;
  desc?: string;
  section?: boolean;
}

interface NavItem {
  label: string;
  href: string;
  icon: any;
  children?: NavChild[];
}

const navItems: NavItem[] = [
  {
    label: "CPA学习",
    href: "/cpa",
    icon: GraduationCap,
    children: [
      { label: "刷题中心", href: "/cpa/bank", desc: "海量真题模拟题，在线刷题实时判分" },
      { label: "错题本 / 学习报告", href: "/cpa/practice-records", desc: "做题记录、错题重练、进度统计与 AI 学习诊断" },
      { label: "模拟考试", href: "/cpa/mock-exam", desc: "按考纲占比智能组卷，实战演练" },
      { label: "知识精讲", href: "/cpa/knowledge", desc: "六科知识点结构化精讲与图谱" },
      { label: "学习计划", href: "/cpa/study-plan", desc: "生成专属备考计划" },
    ],
  },
  {
    label: "CPA工具",
    href: "/cpa/standards",
    icon: Wrench,
    children: [
      { label: "会计准则", href: "/cpa/standards", desc: "企业会计准则、应用指南、解释公告快速查询" },
      { label: "审计准则", href: "/cpa/audit-standards", desc: "中国注册会计师执业准则：审计、审阅、其他鉴证业务与质量控制" },
      { label: "AI 脑图", href: "/mindchat", desc: "AI 对话生成思维导图与全局大脑视图" },
      { label: "审计描述助手", href: "/audit-assistant", desc: "AI 按标准模板生成规范化审计描述文本" },
      { label: "桌面工具", href: "/tool", desc: "CPABuddy 桌面版数据处理与助手" },
    ],
  },
  {
    label: "项目管理",
    href: "/project",
    icon: FolderKanban,
  },
  {
    label: "金融分析",
    href: "/finance",
    icon: TrendingUp,
    children: [
      { label: "行情中心", href: "/finance", desc: "A股/港股/美股/全球指数实时行情" },
      { label: "个股研究 / 我的自选", href: "/finance", desc: "K线、财务、基本面、估值与研报，含我的自选监控" },
      { label: "伯克希尔", href: "/finance/berkshire", desc: "13F 持仓追踪与季度变动分析" },
      { label: "洞察个股", href: "/finance/insights", desc: "AI 洞察驱动的自选跟踪、价值投资榜与短线热点榜" },
    ],
  },
  {
    label: "新闻资讯",
    href: "/cpa/news",
    icon: Newspaper,
    children: [
      { label: "CPA 新闻", href: "/cpa/news", desc: "考试报名、准考证、政策动态" },
      { label: "金融新闻", href: "/finance/news", desc: "A股/港股/美股市场要闻" },
    ],
  },
  {
    label: "反馈",
    href: "/feedback",
    icon: MessageCircle,
  },
];

const mobileOpen = ref(false);
const expandedMobile = ref<string[]>([]);
const activeDropdown = ref<string | null>(null);
const leaveTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const userTools = ref<{ mindchat: boolean; finance: boolean } | null>(null);

const auth = useAuthStore();
const router = useRouter();

const currentUser = computed(() => auth.user);
const adminUser = computed(() => currentUser.value && (currentUser.value.role === "admin" || currentUser.value.username === "leon"));
const isMindChatVisible = computed(() => adminUser.value || userTools.value?.mindchat === true);
const isFinanceVisible = computed(() => adminUser.value || userTools.value?.finance === true);

const filteredNavItems = computed(() =>
  navItems
    .filter((item) => {
      if (item.href === "/finance") return isFinanceVisible.value;
      return true;
    })
    .map((item) => {
      if (!item.children) return item;
      const children = item.children.filter((child) => {
        if (child.href === "/mindchat") return isMindChatVisible.value;
        return true;
      });
      return { ...item, children };
    })
);

watch(mobileOpen, (open) => {
  if (typeof document === "undefined") return;
  if (open) {
    document.body.classList.add("overflow-hidden");
  } else {
    document.body.classList.remove("overflow-hidden");
  }
});

onMounted(async () => {
  if (currentUser.value) {
    try {
      const res = await apiFetch("/api/users/me/tools", { auth: true });
      const tools = res.data || res || [];
      const result = { mindchat: false, finance: false };
      tools.forEach((t: any) => {
        if (t.tool_key === "mindchat") result.mindchat = t.enabled;
        if (t.tool_key === "finance") result.finance = t.enabled;
      });
      userTools.value = result;
    } catch {
      userTools.value = null;
    }
  }
  document.addEventListener("click", onDocumentClick);
});

onUnmounted(() => {
  document.removeEventListener("click", onDocumentClick);
  if (typeof document !== "undefined") {
    document.body.classList.remove("overflow-hidden");
  }
});

function openDropdown(key: string) {
  if (leaveTimer.value) {
    clearTimeout(leaveTimer.value);
    leaveTimer.value = null;
  }
  activeDropdown.value = key;
}

function closeDropdown(delay = 180) {
  leaveTimer.value = setTimeout(() => {
    activeDropdown.value = null;
  }, delay);
}

function toggleDropdown(key: string) {
  if (activeDropdown.value === key) {
    activeDropdown.value = null;
  } else {
    openDropdown(key);
  }
}

function isMobileExpanded(label: string) {
  return expandedMobile.value.includes(label);
}

function toggleMobileExpanded(label: string) {
  if (expandedMobile.value.includes(label)) {
    expandedMobile.value = expandedMobile.value.filter((l) => l !== label);
  } else {
    expandedMobile.value.push(label);
  }
}

function closeMobile() {
  mobileOpen.value = false;
  expandedMobile.value = [];
}

function logout() {
  auth.logout();
  closeMobile();
  router.push("/");
}

function onDocumentClick(event: MouseEvent) {
  const nav = document.querySelector("nav");
  if (nav && !nav.contains(event.target as Node)) {
    activeDropdown.value = null;
  }
}
</script>

<template>
  <nav class="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border shadow-sm">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <!-- Logo -->
        <router-link
          to="/"
          class="flex items-center gap-2.5 text-lg font-bold text-primary hover:text-primary/80 transition shrink-0"
        >
          <div class="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Calculator class="h-4 w-4 text-primary-foreground" />
          </div>
          <span class="hidden sm:inline">CPABuddy</span>
        </router-link>

        <!-- Desktop Navigation -->
        <div class="hidden lg:flex items-center gap-1">
          <div
            v-for="item in filteredNavItems"
            :key="item.href"
            class="relative"
            @mouseenter="item.children && openDropdown(item.label)"
            @mouseleave="closeDropdown(180)"
          >
            <div
              class="flex items-center rounded-md text-sm font-medium transition text-foreground/80 hover:text-primary hover:bg-primary/10"
            >
              <router-link
                :to="item.href"
                class="flex items-center gap-1.5 px-3 py-2"
              >
                <component :is="item.icon" :size="18" />
                <span>{{ item.label }}</span>
              </router-link>
              <button
                v-if="item.children"
                type="button"
                class="px-2 py-2 text-muted-foreground hover:text-foreground focus:outline-none touch-target"
                @click.stop="toggleDropdown(item.label)"
                @mouseenter.stop="item.children && openDropdown(item.label)"
              >
                <ChevronDown
                  :size="14"
                  class="transition-transform"
                  :class="{ 'rotate-180': activeDropdown === item.label }"
                />
              </button>
            </div>

            <div
              v-if="item.children && activeDropdown === item.label"
              class="absolute top-full left-0 pt-2"
            >
              <div class="w-72 bg-background rounded-lg shadow-xl border border-border py-2 animate-in fade-in zoom-in-95 max-h-[80vh] overflow-y-auto">
                <div class="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {{ item.label }}
                </div>
                <template v-for="(child, idx) in item.children" :key="`${child.href || 'child'}-${idx}`">
                  <div
                    v-if="child.section"
                    class="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-primary bg-primary/10 -mx-2 px-4"
                  >
                    {{ child.label }}
                  </div>
                  <router-link
                    v-else
                    :to="child.href || '#'"
                    class="block px-4 py-2 hover:bg-primary/10 rounded-md mx-2 transition group"
                    @click="activeDropdown = null"
                  >
                    <div class="text-sm font-medium text-foreground group-hover:text-primary transition">
                      {{ child.label }}
                    </div>
                    <div v-if="child.desc" class="text-xs text-muted-foreground mt-0.5">{{ child.desc }}</div>
                  </router-link>
                </template>
              </div>
            </div>
          </div>

          <!-- Auth Actions -->
          <div class="ml-4 flex items-center gap-2 border-l border-border pl-4">
            <ThemeToggle />
            <div
              v-if="currentUser"
              class="relative"
              @mouseenter="openDropdown('user-menu')"
              @mouseleave="closeDropdown(200)"
            >
              <button
                type="button"
                class="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-foreground hover:bg-muted transition"
              >
                <UserCircle2 class="h-4 w-4 text-primary" />
                <span>{{ currentUser.username }}</span>
                <ChevronDown :size="14" class="text-muted-foreground" />
              </button>
              <div v-if="activeDropdown === 'user-menu'" class="absolute top-full right-0 pt-2">
                <div class="w-56 bg-background rounded-lg shadow-xl border border-border py-2">
                  <div class="px-4 py-2 border-b border-border">
                    <div class="text-sm font-medium">{{ currentUser.username }}</div>
                    <div class="text-xs text-muted-foreground">{{ currentUser.email || "已登录用户" }}</div>
                  </div>
                  <router-link to="/account" class="flex items-center gap-2 px-4 py-2.5 hover:bg-muted text-sm" @click="activeDropdown = null">
                    <Settings class="h-4 w-4 text-primary" />
                    账号设置
                  </router-link>
                  <router-link v-if="adminUser" to="/admin" class="flex items-center gap-2 px-4 py-2.5 hover:bg-muted text-sm" @click="activeDropdown = null">
                    <ShieldCheck class="h-4 w-4 text-primary" />
                    管理后台
                  </router-link>
                  <button
                    type="button"
                    class="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-muted text-sm text-left"
                    @click="logout"
                  >
                    <LogOut class="h-4 w-4 text-primary" />
                    退出登录
                  </button>
                </div>
              </div>
            </div>
            <template v-else>
              <router-link
                to="/auth/login"
                class="px-4 py-2 text-sm font-medium text-primary hover:text-primary/80 hover:bg-primary/10 rounded-md transition"
              >
                登录
              </router-link>
              <router-link
                to="/auth/register"
                class="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-md transition shadow-sm"
              >
                免费注册
              </router-link>
            </template>
          </div>
        </div>

        <!-- Mobile toggle -->
        <button
          type="button"
          @click="mobileOpen = !mobileOpen"
          class="lg:hidden p-2 rounded-md text-foreground/70 hover:bg-muted touch-target"
          aria-label="Toggle menu"
        >
          <Menu v-if="!mobileOpen" :size="24" />
          <X v-else :size="24" />
        </button>
      </div>
    </div>
  </nav>

  <!-- Mobile Drawer -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="mobileOpen"
        class="fixed inset-0 z-50 lg:hidden"
        @click="closeMobile"
      >
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" />
        <Transition
          enter-active-class="transition-transform duration-300 ease-out"
          leave-active-class="transition-transform duration-200 ease-in"
          enter-from-class="translate-x-full"
          leave-to-class="translate-x-full"
        >
          <div
            v-if="mobileOpen"
            class="absolute top-0 right-0 bottom-0 w-[80%] max-w-[320px] bg-background border-l border-border shadow-2xl flex flex-col safe-bottom"
            @click.stop
          >
            <!-- Drawer header -->
            <div class="flex items-center justify-between px-4 h-16 border-b border-border shrink-0">
              <div class="flex items-center gap-2.5 font-bold text-primary">
                <div class="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Calculator class="h-4 w-4 text-primary-foreground" />
                </div>
                CPABuddy
              </div>
              <button
                type="button"
                class="p-2 rounded-md text-muted-foreground hover:bg-muted touch-target"
                aria-label="Close menu"
                @click="closeMobile"
              >
                <X :size="20" />
              </button>
            </div>

            <!-- Drawer body -->
            <div class="flex-1 overflow-y-auto py-3 px-3">
              <div v-for="item in filteredNavItems" :key="item.href" class="mb-1">
                <div class="flex items-center">
                  <router-link
                    :to="item.href"
                    class="flex-1 flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground/90 hover:bg-primary/10 hover:text-primary transition"
                    @click="closeMobile"
                  >
                    <component :is="item.icon" :size="20" class="shrink-0" />
                    {{ item.label }}
                  </router-link>
                  <button
                    v-if="item.children && item.children.length"
                    type="button"
                    class="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition touch-target"
                    @click="toggleMobileExpanded(item.label)"
                  >
                    <ChevronDown
                      :size="16"
                      class="transition-transform"
                      :class="{ 'rotate-180': isMobileExpanded(item.label) }"
                    />
                  </button>
                </div>
                <div
                  v-if="item.children && item.children.length && isMobileExpanded(item.label)"
                  class="ml-4 pl-4 border-l border-border space-y-1 mt-1"
                >
                  <template v-for="(child, idx) in item.children" :key="`${child.href || 'child'}-${idx}`">
                    <div v-if="child.section" class="px-3 py-2 text-xs font-bold uppercase tracking-wider text-primary">
                      {{ child.label }}
                    </div>
                    <router-link
                      v-else
                      :to="child.href || '#'"
                      class="block px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-primary hover:bg-primary/10 transition"
                      @click="closeMobile"
                    >
                      {{ child.label }}
                    </router-link>
                  </template>
                </div>
              </div>
            </div>

            <!-- Drawer footer -->
            <div class="border-t border-border p-4 space-y-3 shrink-0">
              <div class="flex items-center justify-between">
                <span class="text-sm text-muted-foreground">主题切换</span>
                <ThemeToggle />
              </div>
              <template v-if="currentUser">
                <div class="px-1 py-2 border-b border-border">
                  <div class="text-sm font-medium">{{ currentUser.username }}</div>
                  <div class="text-xs text-muted-foreground">{{ currentUser.email || "已登录用户" }}</div>
                </div>
                <div class="grid grid-cols-2 gap-2">
                  <router-link
                    to="/account"
                    class="flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm border border-border hover:bg-muted transition"
                    @click="closeMobile"
                  >
                    <Settings class="h-4 w-4" />
                    账号
                  </router-link>
                  <router-link
                    v-if="adminUser"
                    to="/admin"
                    class="flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm border border-border hover:bg-muted transition"
                    @click="closeMobile"
                  >
                    <ShieldCheck class="h-4 w-4" />
                    后台
                  </router-link>
                </div>
                <button
                  type="button"
                  class="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-destructive hover:bg-destructive/10 transition"
                  @click="logout"
                >
                  <LogOut class="h-4 w-4" />
                  退出登录
                </button>
              </template>
              <template v-else>
                <div class="grid grid-cols-2 gap-2">
                  <router-link
                    to="/auth/login"
                    class="text-center py-2.5 text-sm font-medium text-primary border border-primary/30 rounded-md hover:bg-primary/10 transition"
                    @click="closeMobile"
                  >
                    登录
                  </router-link>
                  <router-link
                    to="/auth/register"
                    class="text-center py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 transition"
                    @click="closeMobile"
                  >
                    免费注册
                  </router-link>
                </div>
              </template>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
